import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Camera, Upload, Link2, X, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edge-error";
import { errorMessage } from "@/lib/error-message";
import { compressImage, uploadToBucket, signedUrl } from "@/lib/storage";
import { setPendingGeneration, clearPendingGeneration, waitForTryonComplete } from "@/hooks/usePendingGeneration";
import { requestTryonRefund } from "@/lib/refund-tryon";
import { CreditsChip } from "@/components/CreditsChip";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { HighDemandDialog } from "@/components/HighDemandDialog";
import { useCredits } from "@/hooks/useCredits";
import { usePaywallTriggers, type PaywallVariant } from "@/hooks/usePaywallTriggers";
import { useFreeTierGate } from "@/contexts/FreeTierGateContext";
import { PaywallModal } from "@/components/PaywallModal";
import { GenerationOverlay } from "@/components/GenerationOverlay";
import { PhotoExamples } from "@/components/PhotoExamples";
import { GlossyCard, glossyButtonClass } from "@/components/GlossyCard";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import { useIsMobile } from "@/hooks/use-mobile";
import { RecentResultPanel } from "@/components/tryon/RecentResultPanel";
import { TryOnDesktop } from "@/pages/tryon/TryOnDesktop";
import { ProductTemplatePicker } from "@/components/tryon/ProductTemplatePicker";
import type { ProductTemplate } from "@/lib/product-templates";
import { MobileShell } from "@/components/mobile";

import { z } from "zod";

const urlSchema = z.string().trim().url().max(2000);

const STAGES = [
  "Analyzing your photo…",
  "Analyzing the product…",
  "Generating try-on…",
  "Scoring the style…",
];

const TryOn = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { credits, refresh: refreshCredits } = useCredits();
  const { checkBeforeTryon } = usePaywallTriggers();
  const { runGate } = useFreeTierGate();
  const isMobile = useIsMobile();
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyDefaultPack, setBuyDefaultPack] = useState<string | undefined>(undefined);
  const [outOfCreditsOpen, setOutOfCreditsOpen] = useState(false);
  const [highDemandOpen, setHighDemandOpen] = useState(false);
  const [paywall, setPaywall] = useState<PaywallVariant | null>(null);
  const paywallResolverRef = useRef<((bought: boolean) => void) | null>(null);
  const showPaywallAndWait = (variant: PaywallVariant): Promise<boolean> => {
    setPaywall(variant);
    return new Promise<boolean>((resolve) => { paywallResolverRef.current = resolve; });
  };
  const handlePaywallDismiss = () => {
    setPaywall(null);
    const r = paywallResolverRef.current; paywallResolverRef.current = null;
    r?.(false);
  };
  const handlePaywallBuy = (packId: "mini" | "value") => {
    setBuyDefaultPack(packId);
    setBuyOpen(true);
    setPaywall(null);
    const r = paywallResolverRef.current; paywallResolverRef.current = null;
    r?.(true);
  };
  // Bumped after each successful generation so the desktop side panel re-fetches.
  const [resultRefresh, setResultRefresh] = useState(0);

  const [userPhoto, setUserPhoto] = useState<File | null>(null);
  const [userPhotoPreview, setUserPhotoPreview] = useState<string | null>(null);
  const [productUrl, setProductUrl] = useState("");
  const [productImage, setProductImage] = useState<File | null>(null);
  const [productImagePreview, setProductImagePreview] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedUrl, setExtractedUrl] = useState<string | null>(null);
  const [detectedBrand, setDetectedBrand] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [genError, setGenError] = useState<string | null>(null);

  const userInputRef = useRef<HTMLInputElement>(null);
  const productInputRef = useRef<HTMLInputElement>(null);
  const autoFetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchedUrl = useRef<string | null>(null);

  // SEO handled by <SEO /> component below.

  useEffect(() => {
    if (!busy) {
      setStageIdx(0);
      return;
    }
    // Step the staged-progress UI forward sooner so the user feels constant
    // motion. The actual generation timing is unchanged — this is purely the
    // perceived progress between the four labels in STAGES.
    const id = setInterval(() => {
      setStageIdx((i) => Math.min(i + 1, STAGES.length - 1));
    }, 4500);
    return () => clearInterval(id);
  }, [busy]);

  const onPickUserPhoto = async (file?: File) => {
    if (!file) return;
    try {
      // Higher quality for the user photo so the "Before" in the comparison
      // looks as crisp as the AI-generated "After" (which is full-res PNG).
      const compressed = await compressImage(file, 2.5, 2048);
      setUserPhoto(compressed);
      setUserPhotoPreview(URL.createObjectURL(compressed));
    } catch (e) {
      toast({ title: "Couldn't read image", description: String(e), variant: "destructive" });
    }
  };

  const onPickProductImage = async (file?: File) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file, 1, 1024);
      setProductImage(compressed);
      setProductImagePreview(URL.createObjectURL(compressed));
      setExtractedUrl(null);
      setSelectedTemplateId(null);
    } catch (e) {
      toast({ title: "Couldn't read image", description: String(e), variant: "destructive" });
    }
  };

  // Catalog picker — uses the bundled asset URL as the "extracted URL", which
  // re-uses the existing fetch→upload pipeline in generate() with no other
  // changes. The brand is recorded as a detection hint.
  const onPickTemplate = (t: ProductTemplate) => {
    setProductImage(null);
    setProductImagePreview(t.image);
    setExtractedUrl(t.image);
    setProductUrl(t.productUrl);
    setDetectedBrand(t.brand);
    setSelectedTemplateId(t.id);
    lastFetchedUrl.current = t.productUrl;
  };

  const extractFromUrl = async (silent = false) => {
    const parsed = urlSchema.safeParse(productUrl);
    if (!parsed.success) {
      if (!silent) toast({ title: "Enter a valid URL", variant: "destructive" });
      return;
    }
    if (lastFetchedUrl.current === parsed.data) return;
    lastFetchedUrl.current = parsed.data;
    setExtracting(true);
    setExtractedUrl(null);
    setDetectedBrand(null);
    try {
      const { data, error } = await supabase.functions.invoke("extract-product", {
        body: { url: parsed.data },
      });
      if (error) throw error;
      if (!data?.imageUrl) {
        toast({
          title: "Couldn't read this page",
          description:
            "Try the full product URL (not a share link), or upload the image below.",
          variant: "destructive",
        });
      } else {
        setExtractedUrl(data.imageUrl);
        setProductImage(null);
        setProductImagePreview(data.imageUrl);
        if (data.brand && data.brand !== "generic") setDetectedBrand(data.brand);
      }
    } catch (e) {
      const { message } = await readEdgeError(e);
      toast({
        title: "Extraction failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setExtracting(false);
    }
  };

  // Auto-fetch (debounced) when the user pastes/types a valid URL.
  useEffect(() => {
    if (autoFetchTimer.current) clearTimeout(autoFetchTimer.current);
    const trimmed = productUrl.trim();
    if (!trimmed || productImagePreview) return;
    const parsed = urlSchema.safeParse(trimmed);
    if (!parsed.success) return;
    if (lastFetchedUrl.current === parsed.data) return;
    autoFetchTimer.current = setTimeout(() => {
      extractFromUrl(true);
    }, 700);
    return () => {
      if (autoFetchTimer.current) clearTimeout(autoFetchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productUrl, productImagePreview]);

  const generate = async () => {
    if (!user) return;
    if (!userPhoto) {
      toast({ title: "Add your photo first", variant: "destructive" });
      return;
    }
    if (!productImage && !extractedUrl) {
      toast({ title: "Add a product image or link", variant: "destructive" });
      return;
    }
    if ((credits ?? 0) < 1) {
      setOutOfCreditsOpen(true);
      return;
    }

    // Emotional paywall — but "Maybe later" falls through to the ad-gate so
    // free users can still earn this credit by watching an ad.
    const trig = checkBeforeTryon();
    if (trig) {
      const bought = await showPaywallAndWait(trig.variant);
      if (bought) return;
    }

    // Free-tier ad-gate (Android only). 1st free, 2nd & 3rd require ad, 4th = paywall.
    const allowed = await runGate();
    if (!allowed) return;

    setBusy(true);
    setGenError(null);
    // Insert a placeholder row up-front so the generation is recoverable in
    // History even if the user navigates away before it finishes.
    let pendingTryonId: string | null = null;
    let userPath: string | null = null;
    try {
      // 1) Upload user photo
      userPath = await uploadToBucket("user-photos", user.id, userPhoto, "jpg");
      const userUrl = await signedUrl("user-photos", userPath);

      // 2) Upload product image (or fetch the extracted URL)
      let productPath: string | null = null;
      let productSignedUrl: string;
      if (productImage) {
        productPath = await uploadToBucket("product-images", user.id, productImage, "jpg");
        productSignedUrl = await signedUrl("product-images", productPath);
      } else if (extractedUrl) {
        // download remote into bucket so the edge function gets a stable url
        const r = await fetch(extractedUrl);
        if (!r.ok) throw new Error("Couldn't fetch product image");
        const blob = await r.blob();
        productPath = await uploadToBucket("product-images", user.id, blob, "jpg");
        productSignedUrl = await signedUrl("product-images", productPath);
      } else {
        throw new Error("No product image");
      }

      // 2.5) Create the in-progress tryon row so it appears in history.
      const { data: pending, error: pendErr } = await supabase
        .from("tryons")
        .insert({
          user_id: user.id,
          user_photo_url: userPath,
          product_url: productUrl || null,
          product_image_url: productPath,
          status: "processing",
          kind: "tryon",
        })
        .select("id")
        .single();
      if (pendErr) throw pendErr;
      pendingTryonId = pending.id;
      setPendingGeneration({ id: pendingTryonId, kind: "tryon", startedAt: Date.now() });

      // 3) Get profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      // 4) Call generate-tryon — server uploads + finalizes the row, so the
      // result survives the user refreshing or closing this tab mid-generation.
      const invokePromise = supabase.functions.invoke("generate-tryon", {
        body: {
          userPhotoUrl: userUrl,
          productImageUrl: productSignedUrl,
          profile,
          tryonId: pendingTryonId,
        },
      });

      const [terminal, invoke] = await Promise.all([
        waitForTryonComplete(pendingTryonId),
        invokePromise.catch((e) => ({ data: null, error: e })),
      ]);

      if (invoke.error) {
        const info = await readEdgeError(invoke.error);
        const isPremium = Number(profile?.credits_purchased_total ?? 0) > 0;
        // Only show "flooded" dialog on real upstream rate-limit (429), and
        // only for free users. Premium users + 5xx hiccups → real error toast.
        if (!isPremium && info.status === 429) {
          setHighDemandOpen(true);
          clearPendingGeneration();
          return;
        }
        if (info.status === 402) {
          await refreshCredits();
          setOutOfCreditsOpen(true);
          clearPendingGeneration();
          return;
        }
        if (info.status === 401) {
          toast({
            title: "Session expired",
            description: "Please sign in again to continue.",
            variant: "destructive",
          });
          clearPendingGeneration();
          return;
        }
        if (terminal?.status !== "complete") throw new Error(info.message);
      }

      if (terminal?.status === "failed") throw new Error("Generation failed");
      if (!terminal) throw new Error("Generation timed out");

      clearPendingGeneration();
      await refreshCredits();
      // Always redirect to the dedicated Result page so users immediately see
      // the full breakdown (fit score, styling tips, share). The desktop side
      // panel was confusing — direct navigation makes the next step obvious.
      navigate(`/app/result/${pendingTryonId}`);
    } catch (e) {
      // Mark the placeholder as failed AND ask the server to refund the
      // credit if it was already debited (idempotent — server checks).
      if (pendingTryonId) {
        await supabase
          .from("tryons")
          .update({ status: "failed" })
          .eq("id", pendingTryonId)
          .eq("user_id", user.id);
        const refund = await requestTryonRefund(pendingTryonId);
        if (refund.refunded) await refreshCredits();
      }
      clearPendingGeneration();
      const msg = errorMessage(e);
      setGenError(msg);
      toast({
        title: "Try-on failed",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO />
      {false ? (
        <TryOnDesktop
          userPhotoPreview={userPhotoPreview}
          productImagePreview={productImagePreview}
          productUrl={productUrl}
          setProductUrl={setProductUrl}
          detectedBrand={detectedBrand}
          detectedCategory={
            // Best-effort hint from URL / filename / brand BEFORE the AI runs.
            [productUrl, productImage?.name, detectedBrand].filter(Boolean).join(" ") || null
          }
          extractedUrl={extractedUrl}
          extracting={extracting}
          onPickUserPhoto={onPickUserPhoto}
          onPickProductImage={onPickProductImage}
          clearUserPhoto={() => {
            setUserPhoto(null);
            setUserPhotoPreview(null);
          }}
          clearProductImage={() => {
            setProductImage(null);
            setProductImagePreview(null);
            setExtractedUrl(null);
            setSelectedTemplateId(null);
          }}
          extractFromUrl={() => extractFromUrl(false)}
          generate={generate}
          busy={busy}
          stage={STAGES[stageIdx]}
          stageIdx={stageIdx}
          stages={STAGES}
          resultRefresh={resultRefresh}
          onOpenBuyCredits={() => setBuyOpen(true)}
          onPickTemplate={onPickTemplate}
          selectedTemplateId={selectedTemplateId}
        />
      ) : (
    <MobileShell title="Try On" actions={<CreditsChip onClick={() => setBuyOpen(true)} />}>
      <div className="mx-auto w-full max-w-md pt-2 lg:max-w-5xl">
      <div>
      <div className="">
      <p className="mb-5 mt-2 text-sm text-muted-foreground">See how anything looks on you in seconds.</p>

      {/* Your photo (left) + The outfit (right) on desktop, stacked on mobile. */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">

      {/* User photo */}
      <GlossyCard step={1} tone="primary" className="mb-4 lg:mb-0">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-semibold">Your photo</Label>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Full-body or upper-body, good lighting.</p>

        {userPhotoPreview ? (
          <div className="relative mt-3 animate-scale-in">
            <img
              src={userPhotoPreview}
              alt="Your photo"
              className="aspect-[3/4] w-full rounded-xl object-cover shadow-soft ring-1 ring-border"
            />
            {/* Glossy highlight on the image */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-tr from-transparent via-white/5 to-white/15"
            />
            <button
              type="button"
              onClick={() => {
                setUserPhoto(null);
                setUserPhotoPreview(null);
              }}
              className="absolute right-2 top-2 rounded-full bg-background/70 p-1.5 text-foreground backdrop-blur-md transition-all hover:scale-110 hover:bg-background"
              aria-label="Remove photo"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <PhotoExamples className="mt-3" />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <input
                ref={userInputRef}
                type="file"
                name="userPhoto"
                aria-label="Your photo"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={(e) => onPickUserPhoto(e.target.files?.[0])}
              />
              <Button variant="outline" className={glossyButtonClass} onClick={() => {
                if (userInputRef.current) {
                  userInputRef.current.setAttribute("capture", "user");
                  userInputRef.current.click();
                }
              }}>
                <Camera className="mr-2 h-4 w-4" /> Camera
              </Button>
              <Button variant="outline" className={glossyButtonClass} onClick={() => {
                if (userInputRef.current) {
                  userInputRef.current.removeAttribute("capture");
                  userInputRef.current.click();
                }
              }}>
                <Upload className="mr-2 h-4 w-4" /> Upload
              </Button>
            </div>
          </>
        )}
      </GlossyCard>

      {/* Product */}
      <GlossyCard step={2} tone="accent" className="mb-4 lg:mb-0">
        <Label htmlFor="tryon-product-url" className="text-sm font-semibold">The outfit</Label>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Pick from our catalog, paste a link, or upload an image.
        </p>

        {/* Catalog — quickest way to start. Bounded height so the rest of the
            composer stays reachable without long scroll. */}
        {!productImagePreview && (
          <div className="mt-3">
            <ProductTemplatePicker
              onPick={onPickTemplate}
              selectedId={selectedTemplateId}
              compact
            />
          </div>
        )}

        {!productImagePreview && (
          <div className="my-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or paste a link <div className="h-px flex-1 bg-border" />
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <div className="relative flex-1">
            <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="tryon-product-url"
              name="productUrl"
              autoComplete="url"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              placeholder="https://…"
              className="pl-9"
              maxLength={2000}
              type="url"
            />
          </div>
          <Button
            onClick={() => extractFromUrl(false)}
            disabled={extracting || !productUrl}
            className={glossyButtonClass}
          >
            {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Fetch"}
          </Button>
        </div>

        {productImagePreview ? (
          <div className="relative mt-3 animate-scale-in">
            {detectedBrand && extractedUrl && (
              <div className="absolute left-2 top-2 z-10 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white capitalize backdrop-blur-md">
                Detected: {detectedBrand}
              </div>
            )}
            <img
              src={productImagePreview}
              alt="Product"
              className="aspect-square w-full rounded-xl object-contain bg-muted ring-1 ring-border"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-tr from-transparent via-white/5 to-white/15"
            />
            <button
              type="button"
              onClick={() => {
                setProductImage(null);
                setProductImagePreview(null);
                setExtractedUrl(null);
                setSelectedTemplateId(null);
              }}
              className="absolute right-2 top-2 rounded-full bg-background/70 p-1.5 text-foreground backdrop-blur-md transition-all hover:scale-110 hover:bg-background"
              aria-label="Remove product image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="my-3 flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> or upload <div className="h-px flex-1 bg-border" />
            </div>
            <input
              ref={productInputRef}
              type="file"
              name="productImage"
              aria-label="Product image"
              accept="image/*"
              className="hidden"
              onChange={(e) => onPickProductImage(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              className={cn("w-full", glossyButtonClass)}
              onClick={() => productInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" /> Upload product image
            </Button>
          </>
        )}
      </GlossyCard>

      </div>
      {/* /lg:grid wrapper */}

      <div className="mx-auto w-full max-w-md">
      <Button
        className="font-display mt-4 mb-4 h-14 w-full bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))] hover:opacity-95"
        onClick={generate}
        disabled={busy}
      >
        {busy ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {STAGES[stageIdx]}
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-5 w-5" /> Generate Try-On
          </>
        )}
      </Button>

      <p className="mb-2 text-center text-xs text-muted-foreground">
        Usually takes 30–60 seconds · 1 credit per try-on
      </p>
      <p className="mb-6 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        AI may generate inaccurate results. Use as inspiration, not a perfect preview.
      </p>
      </div>
      </div>
      </div>
      </div>

      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} defaultPackId={buyDefaultPack} />
      <BuyCreditsDialog open={outOfCreditsOpen} onOpenChange={setOutOfCreditsOpen} outOfCredits />
      <HighDemandDialog open={highDemandOpen} onOpenChange={setHighDemandOpen} />

      <PaywallModal
        open={!!paywall}
        variant={paywall}
        onClose={() => setPaywall(null)}
        onDismiss={handlePaywallDismiss}
        onBuy={handlePaywallBuy}
      />

      <GenerationOverlay
        open={busy || !!genError}
        stage={STAGES[stageIdx]}
        errorMessage={genError}
        onRetry={() => { setGenError(null); void generate(); }}
        onCancel={() => { setGenError(null); }}
      />
    </MobileShell>
      )}
    </>
  );
};

export default TryOn;
